import { httpService } from '../utils/http';
import { 
  Package, 
  PackageRequest, 
  PackageQueryRequest, 
  PackageVO,
  PaginationParams,
  PaginatedResponse,
  BatchStatusRequest,
  SortRequest,
  Page
} from '../types';

/**
 * 套餐服务类
 * 处理套餐相关的 API 请求
 */
export class PackageService {
  /**
   * 创建套餐
   * @param packageData 套餐数据
   * @returns Promise<Package>
   */
  async createPackage(packageData: PackageRequest): Promise<Package> {
    const response = await httpService.post('/package/create', packageData);
    return response.data;
  }

  /**
   * 使用DTO创建套餐
   * @param packageData 套餐数据
   * @param createBy 创建人
   * @returns Promise<Package>
   */
  async createPackageWithDto(packageData: PackageRequest, createBy: string): Promise<Package> {
    const response = await httpService.post(`/package/create?createBy=${encodeURIComponent(createBy)}`, packageData);
    return response.data;
  }

  /**
   * 更新套餐
   * @param id 套餐ID
   * @param packageData 套餐数据
   * @returns Promise<Package>
   */
  async updatePackage(id: number, packageData: PackageRequest): Promise<Package> {
    const response = await httpService.post(`/package/${id}/update`, packageData);
    return response.data;
  }

  /**
   * 使用DTO更新套餐
   * @param id 套餐ID
   * @param packageData 套餐数据
   * @param updateBy 更新人
   * @returns Promise<Package>
   */
  async updatePackageWithDto(id: number, packageData: PackageRequest, updateBy: string): Promise<Package> {
    const response = await httpService.post(`/package/${id}/update?updateBy=${encodeURIComponent(updateBy)}`, packageData);
    return response.data;
  }

  /**
   * 获取套餐详情
   * @param id 套餐ID
   * @returns Promise<Package>
   */
  async getPackageById(id: number): Promise<Package> {
    const response = await httpService.get(`/package/${id}`);
    return response.data;
  }

  /**
   * 分页查询套餐
   * @param params 分页参数
   * @returns Promise<PaginatedResponse<Package>>
   */
  async getPackages(params: PaginationParams): Promise<PaginatedResponse<Package>> {
    const response = await httpService.get('/package', params);
    return response.data;
  }

  /**
   * 根据套餐组查询套餐
   * @param groupId 套餐组ID
   * @returns Promise<Package[]>
   */
  async getPackagesByGroup(groupId: number): Promise<Page<Package>> {
    const response = await httpService.get(`/package/group/${groupId}/packages`);
    return response.data;
  }

  /**
   * 搜索套餐
   * @param name 套餐名称
   * @returns Promise<Package[]>
   */
  async searchPackages(name: string): Promise<Package[]> {
    const response = await httpService.get('/package/search', { name });
    return response.data;
  }

  /**
   * 条件查询套餐
   * @param queryRequest 查询条件
   * @returns Promise<Page<PackageVO>>
   */
  async queryPackages(queryRequest: PackageQueryRequest): Promise<Page<PackageVO>> {
    const response = await httpService.post('/package/query', queryRequest);
    return response.data;
  }

  /**
   * 启用套餐
   * @param id 套餐ID
   * @param updateBy 更新人
   * @returns Promise<void>
   */
  async enablePackage(id: number, updateBy: string): Promise<void> {
    await httpService.post(`/package/${id}/enable?updateBy=${encodeURIComponent(updateBy)}`);
  }

  /**
   * 禁用套餐
   * @param id 套餐ID
   * @param updateBy 更新人
   * @returns Promise<void>
   */
  async disablePackage(id: number, updateBy: string): Promise<void> {
    await httpService.post(`/package/${id}/disable?updateBy=${encodeURIComponent(updateBy)}`);
  }

  /**
   * 删除套餐
   * @param id 套餐ID
   * @param updateBy 删除人
   * @returns Promise<void>
   */
  async deletePackage(id: number, updateBy: string): Promise<void> {
    await httpService.post(`/package/${id}/delete?updateBy=${encodeURIComponent(updateBy)}`);
  }

  /**
   * 更新套餐排序
   * @param id 套餐ID
   * @param sortOrder 排序值
   * @param updateBy 更新人
   * @returns Promise<void>
   */
  async updatePackageSort(id: number, sortOrder: number, updateBy: string): Promise<void> {
    await httpService.put(`/package/${id}/sort?sortOrder=${sortOrder}&updateBy=${encodeURIComponent(updateBy)}`);
  }

  /**
   * 批量更新套餐状态
   * @param batchRequest 批量请求
   * @returns Promise<void>
   */
  async batchUpdateStatus(batchRequest: BatchStatusRequest): Promise<void> {
    await httpService.put('/package/batch/status', batchRequest);
  }

  /**
   * 统计活跃套餐数量
   * @param groupId 套餐组ID
   * @returns Promise<number>
   */
  async getActivePackageCount(groupId: number): Promise<number> {
    const response = await httpService.get(`/package/group/${groupId}/count`);
    return response.data;
  }
}

// 创建默认实例
export const packageService = new PackageService();

// 导出默认实例
export default packageService; 